/**
 * Which dog you are looking at.
 *
 * RENDERS NOTHING AT ALL for the 174 of 183 households with one dog — not a disabled control, not
 * a single greyed pill. A control that can never be operated still has to be read past, focused
 * past and explained, and it tells the overwhelming majority of clients that there is a feature
 * they are missing out on. 9 households have two dogs; they get the control, everyone else gets
 * their page back.
 *
 * Built on the design system's `.seg` / `.seg-opt` rather than the prototype's bespoke chip
 * buttons, because this is exactly the "one of these" the segmented control exists for — and being
 * native radios means arrow-key traversal, group semantics and `:focus-visible` come from the
 * platform instead of from a `role="radio"` reimplementation with hand-managed roving tabindex.
 * `.seg-opt`'s `<input>` is visually hidden by the design system, not by us.
 */

import type { ReactElement } from 'react'

import { useMe, usePortal } from '../state/portal'

/** Standard visually-hidden recipe. The legend names the group without occupying the layout. */
const HIDDEN_LEGEND = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
} as const

export function DogSwitcher(): ReactElement | null {
  const { state, selectDog } = usePortal()
  const me = useMe()

  const dogs = me.data?.dogs ?? []
  if (dogs.length < 2) return null

  // Mirrors `useSelectedDog`: nothing chosen, or a stale id after a refresh, both fall back to the
  // first dog. If this disagreed with that hook the control would show one dog highlighted while
  // the page below showed the other.
  const chosen = dogs.find((dog) => dog.dogId === state.selectedDogId) ?? dogs[0]

  return (
    <fieldset style={{ border: 0, margin: 0, padding: 0, minInlineSize: 0 }}>
      <legend style={HIDDEN_LEGEND}>Choose which dog to look at</legend>
      <div className="seg">
        {dogs.map((dog) => (
          <label
            key={dog.dogId}
            className="seg-opt"
            /*
              The design system's 7px/13px padding computes to about 29px tall. That is fine for a
              settings row on a desktop page and not fine for the control a client taps on a phone
              while holding a lead, so it is lifted to the 44px floor. Padding, not height alone —
              a min-height on its own leaves the hit area centred and the text clipped.
            */
            style={{ minHeight: '44px', padding: '8px 16px', fontSize: '13.5px', fontWeight: 600 }}
          >
            <input
              type="radio"
              name="ft-dog"
              value={dog.dogId}
              checked={dog.dogId === chosen?.dogId}
              onChange={() => selectDog(dog.dogId)}
            />
            {dog.name}
          </label>
        ))}
      </div>
    </fieldset>
  )
}
